'use client';

import Link from 'next/link';
import { ArrowLeft, Play, Code, Eye } from 'lucide-react';
import StandardLayout from '../../components/layout/StandardLayout';
import { useSite } from '@/lib/site-context';
import '../../components/editor/Documentation.scss';
import CodeExample from '../../components/ui/CodeExample';

export default function GettingStartedPage() {
  const site = useSite();
  const isPython = site.id === 'py';
  const languageName = site.codeLanguageLabel;
  const libraryName = isPython ? 'FullControl' : 'fullcontrol-js';
  const runtimeName = isPython
    ? 'Pyodide (CPython compiled to WebAssembly)'
    : 'a dedicated Web Worker sandbox';
  const codeSample = isPython
    ? `import fullcontrol as fc

layer_height = 0.2
line_width = 0.4
steps = []

steps.append(fc.ExtrusionGeometry(width=line_width, height=layer_height))
square = fc.rectangleXY(
    start_point=fc.Point(x=0, y=0, z=layer_height),
    x_size=20,
    y_size=20
)
steps.extend(square)`
    : `const layerHeight = 0.2;
const lineWidth = 0.4;
const steps = [];

steps.push(new fc.ExtrusionGeometry({ width: lineWidth, height: layerHeight }));
const square = fc.rectangleXY(
  new fc.Point({ x: 0, y: 0, z: layerHeight }),
  20,
  20
);
steps.push(...square);`;

  return (
    <StandardLayout>
      <div className="content-wrapper">
        <div className="docs-container">
          <nav className="docs-nav">
            <Link href="/docs" className="back-link">
              <ArrowLeft size={16} />
              Back to Documentation
            </Link>
          </nav>

          <header className="docs-header">
            <h1>Getting Started</h1>
            <p>Your first {site.displayName} sketch in about five minutes</p>
          </header>

          <div className="docs-content">
            <section className="docs-section">
              <h2>What You'll Learn</h2>
              <p>
                You will create a compact parametric design, execute it entirely in the browser, and inspect the
                resulting toolpath and G-code. The workflow is identical whether you're using
                {isPython ? ' the Pyodide-powered Python runtime' : ' the Web Worker–based JavaScript runtime'},
                so finishing this guide gives you the full mental model for {site.displayName}: edit → run → inspect → share.
              </p>
              <ul>
                <li>How the code editor, controls panel, visualizer, and G-code viewer stay in sync</li>
                <li>Where the {libraryName} scaffold injects helpers like <code>fc</code>, <code>steps</code>, and printer defaults</li>
                <li>How to iterate quickly by tweaking parameters and re-running your sketch</li>
              </ul>
            </section>

            <section className="docs-section">
              <h2>Before You Begin</h2>
              <ul>
                <li>Use the latest Chrome, Edge, or Firefox (Safari works, but Python cold-starts take longer)</li>
                <li>Create or sign in to a {site.displayName} account if you want to save or share sketches</li>
                <li>Plan for ~5 minutes of focused time—ideal for a short break</li>
              </ul>
            </section>

            <section className="docs-section">
              <h2>Step 1: Create a New Sketch</h2>
              <ol>
                <li>
                  Click the{' '}
                  <Link href="/create" className="inline-link">
                    Create New Sketch
                  </Link>{' '}
                  button in the header
                </li>
                <li>You're dropped into the editor with a pre-loaded starter template</li>
                <li>The left pane is your {languageName} editor; right panes handle controls, visualization, and G-code</li>
              </ol>
            </section>

            <section className="docs-section">
              <h2>Step 2: Understand the Template</h2>
              <p>
                The template already imports {libraryName}, initializes <code>steps</code>, and wires the return value into
                our visualization + G-code generation pipeline. All you have to do is add geometry operations:
              </p>
              <CodeExample language={isPython ? 'python' : 'javascript'} code={codeSample} title="Starter Template" />
              <p>
                Each time you click <strong>Run Code</strong>, we execute your snippet inside {runtimeName}, capture the resulting
                <code>steps</code>, and update both the viewer and G-code panel—no local installs or slicers required.
              </p>
            </section>

            <section className="docs-section">
              <h2>Step 3: Tour the Editor</h2>
              <ul>
                <li>
                  <strong>Code Panel</strong>: Monaco editor with {languageName} syntax highlighting, IntelliSense, and handy shortcuts
                  (Ctrl/Cmd + S runs your sketch)
                </li>
                <li>
                  <strong>Controls Panel</strong>: Define sliders/inputs that inject variables before execution—ideal for presentation mode
                </li>
                <li>
                  <strong>3D View</strong>: Inspect extrusion (green) vs travel (gray) moves, drag to orbit, scroll to zoom
                </li>
                <li>
                  <strong>G-code Panel</strong>: Review, copy, or download the generated commands for your printer
                </li>
              </ul>
            </section>

            <section className="docs-section">
              <h2>Step 4: Run, Inspect, Iterate</h2>
              <ol>
                <li>
                  Click <span className="inline-code">Run Code</span> <Play className="inline-icon" /> (or press Ctrl/Cmd + S)
                </li>
                <li>
                  Watch the status pill in the controls panel—first runs may take a few seconds while the runtime warms up
                </li>
                <li>
                  Adjust dimensions, layer heights, or loop counts; re-run until the visualization matches your intent
                </li>
                <li>
                  Promote frequently tweaked values to controls so others can explore the design without touching code
                </li>
              </ol>
              <div className="tip-box">
                <h4>💡 Tip</h4>
                <p>
                  Need to debug? Open the browser console for stack traces, or skim the G-code panel to verify extrusion distances.
                </p>
              </div>
            </section>

            <section className="docs-section">
              <h2>Step 5: Save, Share, Present</h2>
              <ol>
                <li>Give your sketch a memorable title and short summary describing what it generates</li>
                <li>Click <strong>Save</strong> (sign-in required) to store it privately or publish it to your profile</li>
                <li>
                  Use the <strong>Presentation View</strong> link to share an interactive, code-free experience that only exposes the controls you configured
                </li>
                <li>Share the sketch URL with collaborators—they can fork it directly inside the browser</li>
              </ol>
            </section>

            <section className="docs-section">
              <h2>What's Next?</h2>
              <p>Ready to go deeper? Pick one:</p>
              <div className="next-steps">
                <Link href="/docs/basics" className="next-step-card">
                  <Code className="step-icon" />
                  <div>
                    <h4>Master the Basics</h4>
                    <p>Learn the core {libraryName} concepts and helper utilities</p>
                  </div>
                </Link>
                <Link href="/docs/examples" className="next-step-card">
                  <Eye className="step-icon" />
                  <div>
                    <h4>Browse Examples</h4>
                    <p>Study ready-made sketches you can fork and remix</p>
                  </div>
                </Link>
              </div>
            </section>

            <section className="docs-section">
              <h2>Common Issues</h2>
              <div className="faq-item">
                <h4>Code not running?</h4>
                <p>
                  Look for syntax or runtime errors in the console. The editor also underlines issues inline so you can jump straight to the line in question.
                </p>
              </div>
              <div className="faq-item">
                <h4>Nothing in the visualizer?</h4>
                <p>
                  Make sure your sketch produces at least one movement in <code>steps</code>. Zero-length designs (or designs where the extruder stays off)
                  render as blank scenes.
                </p>
              </div>
              <div className="faq-item">
                <h4>Runtime feels slow?</h4>
                <p>
                  Large loops can generate tens of thousands of points. Start simple, then scale up. Python users can also benefit from letting Pyodide warm up once per session.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </StandardLayout>
  );
}
